import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import _ from "lodash";
import classNames from "classnames";
import moment from "moment";
import {
  Page,
  Layout,
  Card,
  Button,
  TextField,
  Tag,
  Badge,
  Avatar,
  Text,
  Grid,
  Box,
  Spinner,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import BannerCustom from "../../components/Banners/BannerCustom";
import {
  downloadMessageFile,
  fetchDialog,
  fetchNewMessagesCount,
  sendMessengerMessage,
  setUnreadToRead,
} from "../../redux/features/messenger/messengerSlice";
import ClipIMG from "img/core/clip.svg";
import "./index.scss";
import PageHelmet from "components/PageHelmet";
import GoBackPageNavigation from "components/GoBackPageNavigation/GoBackPageNavigation";
import useAppDispatch from "hooks/useAppDispatch";

const docsFilesInitial = () => ({ proof_files: [] });

function normalizeDialogFetchResult(result, routeDialogId) {
  if (result == null) return null;

  if (Array.isArray(result)) {
    const match = result.find((d) => String(d?.id) === String(routeDialogId));
    return match ?? (result.length === 1 ? result[0] : null);
  }

  if (typeof result !== "object") {
    return null;
  }

  if (
    result.data != null &&
    typeof result.data === "object" &&
    !Array.isArray(result.data)
  ) {
    return normalizeDialogFetchResult(result.data, routeDialogId);
  }
  if (
    result.result != null &&
    typeof result.result === "object" &&
    !Array.isArray(result.result)
  ) {
    return normalizeDialogFetchResult(result.result, routeDialogId);
  }

  if (result.error && result.id == null && !Array.isArray(result.messages)) {
    return null;
  }

  // Single-dialog JSON: do not treat sibling `items: []` as a paginated list wrapper.
  if (result.id != null || Array.isArray(result.messages)) {
    return result;
  }

  if (result.items && Array.isArray(result.items)) {
    const items = result.items;
    const match = items.find((d) => String(d?.id) === String(routeDialogId));
    return match ?? (items.length === 1 ? items[0] : null);
  }

  return result;
}

function getUserDataForMessage(user, message) {
  const data = {
    avatar: "M",
    userName: "Manager Lappa",
    isCurrentUserMessage: false,
  };

  if (!user) {
    return data;
  }

  if (!user.is_manager && user.id === message.sender_id) {
    data.avatar =
      (user.first_name || "").substr(0, 1) + (user.last_name || "").substr(0, 1);
    data.userName = `${user.first_name} ${user.last_name}`;
  }
  if (user.is_manager && !message.is_manager && message.user_name) {
    const match = message.user_name.match(/[^ ]+ (.+)/);
    const lastPart = match && match[1] ? match[1] : "";
    const secondInitial = lastPart ? lastPart[0] : "";
    data.avatar = message.user_name.charAt(0) + secondInitial;
    data.userName = message.user_name;
  }

  if (message.sender_id === user.id) {
    data.isCurrentUserMessage = true;
    data.userName = "You";
  }

  return data;
}

function attachFiles(docsFiles, text) {
  const formDataFiles = new FormData();
  for (let i = 0; i < docsFiles.proof_files.length; i++) {
    formDataFiles.append(
      "file_" + i,
      docsFiles.proof_files[i],
      docsFiles.proof_files[i].name
    );
  }
  formDataFiles.append("text", text);
  return formDataFiles;
}

function Dialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const dialogId = params.dialogId;

  const user = useSelector((state) => state.user.user);
  // useAppDispatch is already createCustomDispatch(reduxDispatch); do not wrap twice
  // or unwrapResult receives a plain payload and fails (always "Could not load").
  const customDispatch = useAppDispatch();

  const sendMessengerMessageReq = useCallback(
    (p) => customDispatch(sendMessengerMessage(p)),
    [customDispatch]
  );
  const downloadMessageFileReq = useCallback(
    (name) => customDispatch(downloadMessageFile(name)),
    [customDispatch]
  );
  const setUnreadToReadReq = useCallback(
    (id) => customDispatch(setUnreadToRead(id)),
    [customDispatch]
  );
  const fetchNewMessagesCountReq = useCallback(
    () => customDispatch(fetchNewMessagesCount()),
    [customDispatch]
  );
  const fetchDialogReq = useCallback(
    (p) => customDispatch(fetchDialog(p)),
    [customDispatch]
  );

  const [description, setDescription] = useState("");
  const [errorDescription, setErrorDescription] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [fileNameList, setFileNameList] = useState({
    proof_files: [],
  });
  const [sendingError, setSendingError] = useState(null);
  const [dialogLoadState, setDialogLoadState] = useState(() =>
    params.dialogId ? "loading" : "idle"
  );
  const [dialogLoadError, setDialogLoadError] = useState(false);

  const dialogWindowRef = useRef(null);
  const fileUploadInputRef = useRef(null);
  const docsFilesRef = useRef(docsFilesInitial());

  useEffect(() => {
    let cancelled = false;
    if (!dialogId) {
      setDialogLoadState("idle");
      setDialogLoadError(false);
      setDialog(null);
      return undefined;
    }

    setDialog(null);
    setDialogLoadError(false);
    setDialogLoadState("loading");

    setUnreadToReadReq(dialogId)
      .then(() => fetchNewMessagesCountReq())
      .catch(_.noop);

    fetchDialogReq({ dialog_id: dialogId, unread: false })
      .then((result) => {
        if (cancelled) return;
        const normalized = normalizeDialogFetchResult(result, dialogId);
        setDialog(normalized);
        setDialogLoadState("ready");
        setDialogLoadError(!normalized);
      })
      .catch(() => {
        if (!cancelled) {
          setDialog(null);
          setDialogLoadState("ready");
          setDialogLoadError(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [dialogId, setUnreadToReadReq, fetchNewMessagesCountReq, fetchDialogReq]);

  const scrollToBottom = useCallback(() => {
    const el = dialogWindowRef.current;
    if (el) {
      const { scrollHeight, clientHeight } = el;
      el.scrollTop = scrollHeight - clientHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [dialog?.messages, dialog?.id, scrollToBottom]);

  const doDeleteFile = useCallback((fileListName, fileName) => {
    let fileToDelete;
    _.map(docsFilesRef.current[fileListName], (file, index) => {
      if (file.name === fileName) {
        fileToDelete = index;
      }
    });

    if (fileToDelete !== undefined) {
      docsFilesRef.current[fileListName].splice(fileToDelete, 1);
      setFileNameList((prev) => {
        const next = { ...prev, [fileListName]: [...prev[fileListName]] };
        next[fileListName].splice(fileToDelete, 1);
        return next;
      });
    }
  }, []);

  const handleFileSelect = useCallback(
    (docsType) => {
      const input = fileUploadInputRef.current;
      if (!input) return;
      input.click();
      input.onchange = () => {
        const files = input.files;
        setFileNameList((prev) => {
          const nextList = { ...prev, [docsType]: [...prev[docsType]] };
          const bucket = docsFilesRef.current[docsType];
          for (let i = 0; i < files.length; i++) {
            const file = files[i];
            let fileToDelete;
            _.map(bucket, (f, index) => {
              if (f.name === file.name) fileToDelete = index;
            });
            if (fileToDelete !== undefined) {
              bucket.splice(fileToDelete, 1);
              nextList[docsType].splice(fileToDelete, 1);
            }
            bucket.push(file);
            nextList[docsType].push(file.name);
          }
          return nextList;
        });
      };
    },
    []
  );

  const downloadFile = useCallback(
    (data) => {
      downloadMessageFileReq(data).then((toDownload) => {
        const from = data.indexOf("/") + 1;
        const to = data.lastIndexOf(".");
        const name = data.substring(from, to);
        const a = document.createElement("a");
        a.setAttribute("href", toDownload.link);
        a.setAttribute("download", name);
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(toDownload.link);
        document.body.removeChild(a);
      });
    },
    [downloadMessageFileReq]
  );

  const createMessage = useCallback(
    (event) => {
      event.preventDefault();
      if (!dialog?.id) {
        return;
      }
      if (!description) {
        setErrorDescription(t("dialog.addCommMess"));
        return;
      }

      const formData = {
        dialogId: +dialog.id,
        text: description,
      };
      const files = attachFiles(docsFilesRef.current, description);

      sendMessengerMessageReq({ ...formData, data: files })
        .then(() => {
          docsFilesRef.current = docsFilesInitial();
          setFileNameList({ proof_files: [] });
          setDescription("");
          setSendingError(null);
          return fetchDialogReq({ dialog_id: dialog.id, unread: false });
        })
        .then((result) => {
          const normalized = normalizeDialogFetchResult(result, String(dialog.id));
          setDialog(normalized ?? result);
        })
        .catch((resp) => {
          setSendingError(resp);
        });
    },
    [
      description,
      dialog,
      fetchDialogReq,
      sendMessengerMessageReq,
      t,
    ]
  );

  const renderCaseNumber = useCallback(() => {
    const rawId = dialog?.id ?? dialogId;
    if (rawId == null || rawId === "") {
      return "Request";
    }
    return `Request ${String(rawId).padStart(10, "0")}`;
  }, [dialog, dialogId]);

  const renderStatusBadge = useCallback(
    (value) => {
      let status = "";
      const normalized = (value ?? "").toString().toLowerCase();
      switch (normalized) {
        case "info":
          status = <Badge tone={"info"}>{t("dialog.info")}</Badge>;
          break;
        case "action required":
          status = <Badge tone={"warning"}>{t("dialog.actionR")}</Badge>;
          break;
        case "pending lovat action":
          status = <Badge tone={"success"}>{t("dialog.pendingLovat")}</Badge>;
          break;
        default:
          status = <Badge>{t("dialog.answered")}</Badge>;
      }
      return status;
    },
    [t]
  );

  const renderMultipleFileSelect = useCallback(
    (docType) => (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <Button onClick={() => handleFileSelect(docType)}>
            Add file
            <input
              type="file"
              multiple
              style={{ display: "none" }}
              ref={fileUploadInputRef}
            />
          </Button>
          <Text color="subdued">{t("dialog.chooseFiles")}</Text>
        </div>
        <div>
          {_.map(fileNameList.proof_files, (fileName, index) => (
            <div
              style={{
                display: "inline-block",
                margin: "5px",
              }}
              key={index}
            >
              <Tag
                onRemove={() => {
                  doDeleteFile("proof_files", fileName);
                }}
              >
                {fileName}
              </Tag>
            </div>
          ))}
        </div>
      </>
    ),
    [doDeleteFile, fileNameList.proof_files, handleFileSelect, t]
  );

  const onNavigateBack = useCallback(() => {
    const fromRoute = location.state?.supportRestoreCompany;
    let supportRestoreCompany =
      fromRoute?.id != null
        ? { id: fromRoute.id, name: fromRoute.name ?? "" }
        : undefined;

    if (!supportRestoreCompany && dialog?.company_id != null) {
      supportRestoreCompany = {
        id: dialog.company_id,
        name:
          dialog.company_name ??
          dialog.company?.name ??
          "",
      };
    }

    navigate("/help", {
      state: {
        defaultTab: "support",
        ...(supportRestoreCompany?.id != null
          ? { supportRestoreCompany }
          : {}),
      },
    });
  }, [navigate, location.state, dialog]);

  const messages = Array.isArray(dialog?.messages) ? dialog.messages : null;

  return (
    <Page
      title={
        <GoBackPageNavigation
          onBack={onNavigateBack}
          content={
            <Text variant="heading3xl" as="span">
              {renderCaseNumber()}
            </Text>
          }
        />
      }
      fullWidth
    >
      <PageHelmet title={"Support chat"} />

      {dialogLoadState === "loading" && (
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <Box padding="400">
                <Spinner
                  accessibilityLabel={t("common.loading", {
                    defaultValue: "Loading",
                  })}
                />
              </Box>
            </Card>
          </Layout.Section>
        </Layout>
      )}

      {dialogLoadState === "ready" && dialogLoadError && (
        <Layout>
          <Layout.Section>
            <BannerCustom
              status="critical"
              message={t("dialog.loadFailed", {
                defaultValue:
                  "Could not load this request. Try again or go back to the list.",
              })}
            />
          </Layout.Section>
        </Layout>
      )}

      {dialog && (
        <Layout>
          <Layout.Section>
            <Grid>
              <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 8, xl: 8 }}>
                <Card>
                  <div>
                    <Text variant="headingLg">Support chat</Text>
                  </div>
                  <Box paddingBlockEnd="500">
                    <div
                      className="dialog__messages"
                      ref={dialogWindowRef}
                    >
                      {messages &&
                        messages.map((message) => {
                          const data = getUserDataForMessage(user, message);
                          return (
                            <div
                              key={message.id}
                              className={classNames("dialog__message", {
                                myMessage: data.isCurrentUserMessage,
                              })}
                            >
                              {!data.isCurrentUserMessage && (
                                <Avatar initials={data.avatar} />
                              )}

                              <div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: data.isCurrentUserMessage
                                      ? "space-between"
                                      : "start",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Text>{data.userName}</Text>
                                  </div>

                                  <div className="dialog__message-date">
                                    <Text color="subdued" variant="bodySm">
                                      {moment(message.created).format("LLL")}
                                    </Text>
                                  </div>
                                </div>
                                <p
                                  className="dialog__message-text"
                                  dangerouslySetInnerHTML={{
                                    __html: String(message.text ?? "").replace(
                                      /\n/g,
                                      "<br>"
                                    ),
                                  }}
                                />
                                {Array.isArray(message.files) &&
                                  message.files.map((f, index) => (
                                    <div
                                      key={`${index}-file`}
                                      className="dialog__message-file"
                                    >
                                      <img
                                        className="Button__Prefix"
                                        src={ClipIMG}
                                        alt="icon save"
                                      />
                                      <a
                                        className="Polaris-Link"
                                        onClick={() => downloadFile(f)}
                                      >
                                        {f.slice(5)}
                                      </a>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </Box>
                  <div>
                    {sendingError && (
                      <BannerCustom status={"critical"} message={sendingError} />
                    )}
                    <form onSubmit={createMessage}>
                      <Box display="flex" flexDirection="column" gap="400">
                        <TextField
                          id="desc"
                          name="description"
                          placeholder="Write a message"
                          multiline={6}
                          value={description}
                          error={errorDescription}
                          onChange={(v) => {
                            setDescription(v);
                            setErrorDescription(false);
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "flex-end",
                            gap: "16px",
                            paddingTop: 10,
                            width: "100%",
                          }}
                        >
                          <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                            <div style={{ paddingBottom: 8 }}>
                              {renderMultipleFileSelect("proof_files")}
                            </div>
                          </div>
                          <div style={{ flex: "0 0 auto", marginLeft: "auto" }}>
                            <Button variant={"primary"} submit size="large">
                              Send
                            </Button>
                          </div>
                        </div>
                      </Box>
                    </form>
                  </div>
                </Card>
              </Grid.Cell>
              <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 6, lg: 4, xl: 4 }}>
                <Card sectioned>
                  <Box display="flex" flexDirection="column" gap="6">
                    <Box display="flex" flexDirection="column" gap="2">
                      <Text fontWeight="medium">Case number</Text>
                      <Text color="subdued">{renderCaseNumber()}</Text>
                    </Box>
                    <Box display="flex" flexDirection="column" gap="2">
                      <Text fontWeight="medium">Status</Text>
                      {renderStatusBadge(dialog.dialog_status)}
                    </Box>
                    <Box display="flex" flexDirection="column" gap="2">
                      <Text fontWeight="medium">Date update</Text>
                      <Text color="subdued">
                        {moment(dialog.modified).format("DD-MM-YYYY hh:mm")}
                      </Text>
                    </Box>
                    <Box display="flex" flexDirection="column" gap="2">
                      <Text fontWeight="medium">Case group</Text>
                      <Text color="subdued">
                        <span style={{ textTransform: "capitalize" }}>
                          {dialog.case_group}
                        </span>
                      </Text>
                    </Box>
                  </Box>
                </Card>
              </Grid.Cell>
            </Grid>
          </Layout.Section>
        </Layout>
      )}
    </Page>
  );
}

export default Dialog;
